export type WithRetryOptions = {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  shouldRetry?: (err: unknown) => boolean;
};

const NON_RETRYABLE_PG_PREFIXES = ["PGRST", "22", "23", "42"];

function isNonRetryableError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const code = (err as { code?: unknown }).code;
  if (typeof code !== "string") return false;
  return NON_RETRYABLE_PG_PREFIXES.some((prefix) => code.startsWith(prefix));
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  options?: WithRetryOptions
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelayMs = 1000,
    maxDelayMs = 10000,
    shouldRetry,
  } = options ?? {};

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;

      if (attempt === maxRetries) break;

      if (shouldRetry ? !shouldRetry(err) : isNonRetryableError(err)) break;

      // Don't retry if we're offline — fail fast
      if (typeof navigator !== "undefined" && !navigator.onLine) break;

      const delay = Math.min(baseDelayMs * 2 ** attempt, maxDelayMs);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
